import React, { useEffect, useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';

interface AddContactDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (input: {
    phone: string;
    name?: string;
    tags?: string[];
  }) => Promise<void>;
}

const AddContactDialog: React.FC<AddContactDialogProps> = ({
  open,
  onClose,
  onCreate
}) => {
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [tags, setTags] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPhone('');
      setName('');
      setTags('');
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async () => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 8) {
      setError('Enter a valid phone number with country code');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onCreate({
        phone: digits,
        name: name.trim() || undefined,
        tags: tags
          .split(',')
          .map((t) => t.trim().toLowerCase().replace(/\s+/g, '-'))
          .filter(Boolean)
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Could not add this contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">
            Add contact
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 p-4">
          <label className="block">
            <span className="text-xs font-medium text-gray-700">
              WhatsApp number
            </span>
            <input
              type="tel"
              autoFocus
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98765 43210"
              className="mt-1 w-full rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
            />
            <span className="mt-1 block text-[11px] text-gray-500">
              A 10-digit number is assumed to be Indian (+91).
            </span>
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-700">
              Name (optional)
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-700">
              Tags (comma separated)
            </span>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="football, noida"
              className="mt-1 w-full rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </label>

          <p className="flex items-start gap-1.5 rounded bg-warning-50 p-2 text-[11px] text-black">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
            Consent is <strong>not recorded</strong> for contacts added here —
            you can still message them, but only with an approved template, and
            only where you actually have consent.
          </p>

          {error && <p className="text-xs text-danger-600">{error}</p>}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || !phone.trim()}
            className="w-full rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Adding…' : 'Add contact'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddContactDialog;
